import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useBlocker, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import GenerationScreen from "@/components/GenerationScreen";
import PreviewScreen from "@/components/PreviewScreen";
import StoriesScreen from "@/components/StoriesScreen";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RUNNING_STATUSES = ["extracting", "generating"];

export default function RecitPanel({ sessionId, onNavigateInterview }) {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [autobiography, setAutobiography] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [stories, setStories] = useState([]);
  const [detectedStyle, setDetectedStyle] = useState(null);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [promptVariants, setPromptVariants] = useState([]);
  const [selectedPromptVariantId, setSelectedPromptVariantId] = useState("classique");
  const [defaultPromptVariantId, setDefaultPromptVariantId] = useState("classique");
  const [storiesLoading, setStoriesLoading] = useState(false);
  const [storiesLoadFailed, setStoriesLoadFailed] = useState(false);

  const blocker = useBlocker(RUNNING_STATUSES.includes(session?.status));

  useEffect(() => {
    if (blocker.state !== "blocked") return;
    if (window.confirm(
      "Une génération est en cours. Quitter la page arrêtera seulement le suivi à l'écran ; " +
      "la génération continuera côté serveur. Continuer ?"
    )) {
      blocker.proceed();
    } else {
      blocker.reset();
    }
  }, [blocker]);

  const loadStories = useCallback(async () => {
    setStoriesLoading(true);
    setStoriesLoadFailed(false);
    try {
      const res = await axios.get(`${API}/stories/${sessionId}`);
      setStories(res.data.stories || []);
      setDetectedStyle(res.data.style || null);
      return true;
    } catch (error) {
      console.error("Failed to load stories:", error);
      setStoriesLoadFailed(true);
      toast.error("Erreur lors du chargement des histoires");
      return false;
    } finally {
      setStoriesLoading(false);
    }
  }, [sessionId]);

  const loadVersions = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await axios.get(`${API}/versions/${sessionId}`);
      setVersions(res.data.versions || []);
      setCurrentVersion(res.data.current_version || 1);
    } catch (error) {
      console.error("Error loading versions:", error);
    }
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/session/${sessionId}`);
        if (cancelled) return;
        const loadedSession = res.data;
        setSession(loadedSession);
        setAutobiography(loadedSession.autobiography || "");
        setChatHistory(loadedSession.chat_history || []);
        setVersions(loadedSession.versions || []);
        setCurrentVersion(loadedSession.current_version || 1);
        setProgress(loadedSession.progress || 0);
        setStatusMessage(loadedSession.message || "");
        if (loadedSession.status === "stories_ready") await loadStories();
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to load session:", error);
          toast.error("Erreur lors du chargement du récit");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadStories, sessionId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/prompt-variants`);
        if (cancelled) return;
        const variants = res.data?.variants || [];
        const defaultVariant = res.data?.default || "classique";
        setPromptVariants(variants);
        setDefaultPromptVariantId(defaultVariant);
        setSelectedPromptVariantId((current) => (
          variants.some((variant) => variant.id === current) ? current : defaultVariant
        ));
      } catch (error) {
        console.warn("Could not fetch prompt variants", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!RUNNING_STATUSES.includes(session?.status) || !sessionId) return undefined;

    const pollInterval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/progress/${sessionId}`);
        const next = res.data;
        setProgress(next.progress);
        setStatusMessage(next.message);

        if (next.status === "stories_ready") {
          clearInterval(pollInterval);
          await loadStories();
          setSession((current) => ({ ...current, ...next }));
        } else if (next.status === "ready") {
          setAutobiography(next.autobiography);
          setSession((current) => ({ ...current, ...next }));
          clearInterval(pollInterval);
          toast.success("Autobiographie générée !");
          loadVersions();
        } else if (next.status === "error") {
          clearInterval(pollInterval);
          setSession((current) => ({ ...current, ...next }));
          toast.error(next.message || "Erreur lors de la génération");
        } else {
          setSession((current) => ({ ...current, ...next }));
        }
      } catch (error) {
        console.error(error);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [loadStories, loadVersions, session?.status, sessionId]);

  const handleStartExtraction = async () => {
    const genFormData = new FormData();
    genFormData.append("session_id", sessionId);
    genFormData.append("prenom_nom", session?.prenom_nom || "[Prénom Nom]");
    genFormData.append("activite", session?.activite || "[Activité professionnelle]");
    genFormData.append("public_cible", session?.public_cible || "[Public cible]");
    genFormData.append("instructions_supp", session?.instructions_supp || "");

    try {
      const genRes = await axios.post(`${API}/generate`, genFormData);
      if (genRes.data.success) {
        setSession((current) => ({ ...current, status: "extracting" }));
        setProgress(0);
        setStatusMessage("Démarrage...");
      } else {
        toast.error(genRes.data.error || "Erreur lors du démarrage");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors du démarrage");
    }
  };

  const handleGenerateFromStories = async (selection) => {
    try {
      setSession((current) => ({ ...current, status: "generating" }));
      setProgress(50);
      setStatusMessage("Rédaction de l'autobiographie...");
      await axios.post(`${API}/generate-from-stories`, {
        session_id: sessionId,
        prompt_variant_id: selectedPromptVariantId,
        ...selection,
      });
    } catch (error) {
      toast.error("Erreur lors du lancement de la génération");
      setSession((current) => ({ ...current, status: "stories_ready" }));
    }
  };

  const handleChatMessage = async (message) => {
    if (!sessionId || !message.trim()) return;
    setIsAdjusting(true);
    setChatHistory((previous) => [...previous, { role: "user", text: message }]);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message });
      if (res.data.success) {
        setChatHistory((previous) => [
          ...previous,
          { role: "assistant", text: res.data.response },
        ]);
      } else {
        toast.error(res.data.error || "Erreur");
        setChatHistory((previous) => [
          ...previous,
          { role: "assistant", text: `Erreur : ${res.data.error}` },
        ]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setIsAdjusting(false);
    }
  };

  const handleRegenerateWithContext = async () => {
    if (!sessionId) return;
    setIsAdjusting(true);
    try {
      const res = await axios.post(`${API}/regenerate-with-context`, {
        session_id: sessionId,
        prompt_variant_id: selectedPromptVariantId,
      });
      if (res.data.success) {
        setAutobiography(res.data.autobiography);
        setCurrentVersion(res.data.version);
        setChatHistory([{
          role: "assistant",
          text: `✓ Version ${res.data.version} générée avec vos retours.`,
        }]);
        toast.success("Autobiographie régénérée !");
        loadVersions();
      } else {
        toast.error(res.data.error || "Erreur lors de la régénération");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la régénération");
    } finally {
      setIsAdjusting(false);
    }
  };

  const handleDirectAdjustment = async (demande) => {
    if (!sessionId || !demande.trim()) return;
    setIsAdjusting(true);
    setChatHistory((previous) => [...previous, { role: "user", text: demande }]);
    try {
      const res = await axios.post(`${API}/adjust`, { session_id: sessionId, demande });
      if (res.data.success) {
        setAutobiography(res.data.autobiography);
        setCurrentVersion(res.data.version || currentVersion + 1);
        setChatHistory((previous) => [
          ...previous,
          { role: "assistant", text: "Modification appliquée." },
        ]);
        toast.success("Modification appliquée");
        loadVersions();
      } else {
        toast.error(res.data.error || "Erreur lors de la modification");
        setChatHistory((previous) => [
          ...previous,
          { role: "assistant", text: `Erreur : ${res.data.error}` },
        ]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la modification");
    } finally {
      setIsAdjusting(false);
    }
  };

  const handleRestoreVersion = async (versionNum) => {
    if (!sessionId) return;
    try {
      const res = await axios.post(`${API}/restore-version`, {
        session_id: sessionId,
        version: versionNum,
      });
      if (res.data.success) {
        setAutobiography(res.data.autobiography);
        setCurrentVersion(res.data.version);
        toast.success(`Version ${versionNum} restaurée`);
      } else {
        toast.error(res.data.error || "Erreur lors de la restauration");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la restauration");
    }
  };

  const handleCopy = useCallback(() => {
    const textArea = document.createElement("textarea");
    textArea.value = autobiography;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand("copy");
      toast.success("Texte copié dans le presse-papiers");
    } catch (error) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(autobiography)
          .then(() => toast.success("Texte copié dans le presse-papiers"))
          .catch(() => toast.error("Impossible de copier. Sélectionnez le texte manuellement."));
      } else {
        toast.error("Impossible de copier. Sélectionnez le texte manuellement.");
      }
    }
    document.body.removeChild(textArea);
  }, [autobiography]);

  const handleDownloadPdf = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/download-pdf/${sessionId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "autobiographie.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erreur lors du téléchargement du PDF");
    }
  }, [sessionId]);

  const handleSaveEdit = async (newContent) => {
    try {
      const res = await axios.post(`${API}/save-edit/${sessionId}`, { content: newContent });
      setAutobiography(newContent);
      setCurrentVersion(res.data.version);
      await loadVersions();
      toast.success("Modifications sauvegardees");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  if (!session) {
    return <p className="p-6 text-muted-foreground">Chargement du récit...</p>;
  }

  const hasSources = session.has_sources ?? Boolean(session.sources_text);
  const noTranscriptionPanel = (
    <div className="mx-auto max-w-xl space-y-4 p-8 text-center">
      <p className="text-muted-foreground">
        Aucune transcription déposée pour ce dossier. Le récit se lance depuis l'onglet
        Interview.
      </p>
      <Button onClick={onNavigateInterview}>Déposer la transcription</Button>
    </div>
  );

  if (session.status === "idle") {
    if (!hasSources) return noTranscriptionPanel;
    return (
      <div className="mx-auto max-w-xl space-y-4 p-8 text-center">
        <p className="text-muted-foreground">
          La transcription est prête. Lancez l'extraction pour identifier les histoires.
        </p>
        <Button onClick={handleStartExtraction}>Lancer l'extraction</Button>
      </div>
    );
  }

  if (RUNNING_STATUSES.includes(session.status)) {
    return (
      <GenerationScreen
        progress={progress}
        statusMessage={statusMessage}
        onBackToDashboard={() => navigate("/")}
      />
    );
  }

  if (session.status === "stories_ready") {
    if (storiesLoading || storiesLoadFailed) {
      return (
        <div className="relative">
          <GenerationScreen
            progress={progress}
            statusMessage={storiesLoadFailed
              ? "Impossible de charger les histoires."
              : statusMessage}
            onBackToDashboard={() => navigate("/")}
          />
          {storiesLoadFailed && (
            <div className="absolute inset-x-0 bottom-12 z-10 flex justify-center">
              <Button onClick={loadStories}>Réessayer</Button>
            </div>
          )}
        </div>
      );
    }
    return (
      <StoriesScreen
        stories={stories}
        style={detectedStyle}
        onGenerate={handleGenerateFromStories}
        onBackToDashboard={() => navigate("/")}
        isLoading={false}
        promptVariants={promptVariants}
        selectedPromptVariantId={selectedPromptVariantId}
        onPromptVariantChange={setSelectedPromptVariantId}
        defaultPromptVariantId={defaultPromptVariantId}
      />
    );
  }

  if (["ready", "adjusting"].includes(session.status)) {
    return (
      <PreviewScreen
        autobiography={autobiography}
        chatHistory={chatHistory}
        onChatMessage={handleChatMessage}
        onRegenerateWithContext={handleRegenerateWithContext}
        onDirectAdjust={handleDirectAdjustment}
        onCopy={handleCopy}
        onBackToDashboard={() => navigate("/")}
        isAdjusting={isAdjusting || session.status === "adjusting"}
        versions={versions}
        currentVersion={currentVersion}
        onRestoreVersion={handleRestoreVersion}
        onSaveEdit={handleSaveEdit}
        onDownloadPdf={handleDownloadPdf}
        promptVariants={promptVariants}
        selectedPromptVariantId={selectedPromptVariantId}
        onPromptVariantChange={setSelectedPromptVariantId}
      />
    );
  }

  if (session.status === "error") {
    return (
      <div className="mx-auto max-w-xl space-y-4 p-8 text-center">
        <p className="text-destructive">{session.message || "Erreur lors de la génération"}</p>
        <div className="flex justify-center gap-2">
          <Button onClick={handleStartExtraction}>Relancer</Button>
          <Button variant="outline" onClick={onNavigateInterview}>
            Redéposer la transcription
          </Button>
        </div>
      </div>
    );
  }

  return noTranscriptionPanel;
}
